import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, FileText, Cookie, Accessibility, Building2, Lock, ScrollText, Send, Check, AlertCircle,
} from "lucide-react";
import { Logo } from "../components/Brand";
import { useApp, API } from "../AppContext";
import { PROGERE_LEGAL } from "../config/progere-legal";
import { openConsentPreferences } from "../components/ProgereConsentManager";
import ProgereLegalFooter from "../components/ProgereLegalFooter";

/** Shell commun avec header sticky + footer légal universel */
const LegalShell = ({ children, icon: Icon, title, testId }) => {
  const { lang, setLang } = useApp();
  const isFr = lang === "fr";
  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--pg-bg-main)" }} data-testid={testId}>
      <header className="sticky top-0 z-40" style={{ background: "var(--pg-topbar)", borderBottom: "1px solid var(--pg-border)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/"><Logo size="sm" /></Link>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setLang(lang === "fr" ? "en" : "fr")}
              className="text-xs font-semibold px-3 py-1.5 rounded-full"
              style={{ border: "1px solid var(--pg-border-strong)", color: "var(--pg-text-secondary)" }}
              data-testid="legal-lang-toggle"
            >
              {lang === "fr" ? "EN" : "FR"}
            </button>
            <Link
              to="/"
              className="text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
              style={{ color: "var(--pg-text-secondary)" }}
            >
              <ArrowLeft size={12} /> {isFr ? "Accueil" : "Home"}
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
          <div className="flex items-center gap-3 mb-8">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{
                background: "color-mix(in srgb, var(--pg-gold) 12%, transparent)",
                border: "1px solid color-mix(in srgb, var(--pg-gold) 30%, transparent)",
              }}
            >
              <Icon size={22} style={{ color: "var(--pg-gold)" }} />
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black" style={{ color: "var(--pg-text-primary)" }}>{title}</h1>
          </div>
          <div
            className="rounded-2xl p-6 sm:p-10"
            style={{ background: "var(--pg-bg-card)", border: "1px solid var(--pg-border)", boxShadow: "var(--pg-shadow)" }}
          >
            {children}
          </div>
        </div>
      </main>

      <ProgereLegalFooter />
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="font-heading text-xl sm:text-2xl font-bold mb-3" style={{ color: "var(--pg-gold)" }}>{title}</h2>
    <div className="text-sm leading-relaxed space-y-3" style={{ color: "var(--pg-text-secondary)" }}>{children}</div>
  </section>
);

// ============================================================================
// TÉMOINS / COOKIES
// ============================================================================
export function CookiesPage() {
  const { lang } = useApp();
  const isFr = lang === "fr";
  return (
    <LegalShell icon={Cookie} title={isFr ? "Politique des témoins (cookies)" : "Cookie policy"} testId="page-cookies">
      <p className="text-xs uppercase tracking-widest mb-6" style={{ color: "var(--pg-text-muted)" }}>
        {isFr ? "Dernière mise à jour" : "Last updated"} : {PROGERE_LEGAL.versions.lastUpdated} · v{PROGERE_LEGAL.versions.cookies}
      </p>

      <Section title={isFr ? "Qu'est-ce qu'un témoin ?" : "What is a cookie?"}>
        <p>{isFr
          ? "Un témoin (cookie) est un petit fichier stocké dans votre navigateur pour retenir vos préférences et permettre à l'application de fonctionner. Conformément à la Loi 25 (Québec), nous demandons votre consentement explicite pour tout témoin non essentiel."
          : "A cookie is a small file stored in your browser to remember your preferences and enable the application to work. Under Quebec's Law 25, we ask for your explicit consent for any non-essential cookie."}
        </p>
      </Section>

      <Section title={isFr ? "Catégories que nous utilisons" : "Categories we use"}>
        <div className="space-y-3">
          {PROGERE_LEGAL.cookieCategories.map((cat) => (
            <div key={cat.id} className="p-4 rounded-xl" style={{ background: "var(--pg-bg-tertiary)", border: "1px solid var(--pg-border)" }}>
              <div className="flex items-center gap-2 mb-1">
                <div className="font-semibold" style={{ color: "var(--pg-text-primary)" }}>
                  {isFr ? cat.label.fr : cat.label.en}
                </div>
                {cat.required && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "var(--pg-border-strong)", color: "var(--pg-text-muted)" }}>
                    {isFr ? "Obligatoire" : "Required"}
                  </span>
                )}
              </div>
              <p className="text-xs">{isFr ? cat.description.fr : cat.description.en}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title={isFr ? "Gérer vos préférences" : "Manage your preferences"}>
        <p>{isFr
          ? "Vous pouvez revoir ou modifier vos choix à tout moment :"
          : "You can review or change your choices at any time:"}
        </p>
        <button
          onClick={openConsentPreferences}
          data-testid="cookies-manage-btn"
          className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full"
          style={{ background: "var(--pg-gold)", color: "var(--pg-text-inverse)" }}
        >
          {isFr ? "Gérer mes témoins" : "Manage my cookies"}
        </button>
      </Section>

      <Section title={isFr ? "Durée de conservation" : "Retention"}>
        <p>{isFr
          ? "Vos choix de consentement sont conservés dans un registre chiffré pendant 3 ans (obligation de preuve — Loi 25 art. 8.1)."
          : "Your consent choices are stored in an encrypted registry for 3 years (proof requirement — Law 25 art. 8.1)."}
        </p>
      </Section>
    </LegalShell>
  );
}

// ============================================================================
// ACCESSIBILITÉ
// ============================================================================
export function AccessibilityPage() {
  const { lang } = useApp();
  const isFr = lang === "fr";
  return (
    <LegalShell icon={Accessibility} title={isFr ? "Déclaration d'accessibilité" : "Accessibility statement"} testId="page-accessibility">
      <p className="text-xs uppercase tracking-widest mb-6" style={{ color: "var(--pg-text-muted)" }}>
        {isFr ? "Version" : "Version"} {PROGERE_LEGAL.versions.accessibility} · {PROGERE_LEGAL.versions.lastUpdated}
      </p>
      <Section title={isFr ? "Notre engagement" : "Our commitment"}>
        <p>{isFr
          ? "PROGÈRE s'engage à rendre son application accessible au plus grand nombre, y compris aux personnes en situation de handicap, conformément aux standards WCAG 2.1 niveau AA et à la Loi assurant l'exercice des droits des personnes handicapées (RLRQ, c. E-20.1)."
          : "PROGÈRE is committed to making its application accessible, including to people with disabilities, in accordance with WCAG 2.1 Level AA and Quebec's accessibility legislation."}
        </p>
      </Section>
      <Section title={isFr ? "Fonctionnalités d'accessibilité" : "Accessibility features"}>
        <ul className="list-disc pl-6 space-y-1">
          <li>{isFr ? "Contraste minimum 4.5:1 (texte) / 3:1 (grandes tailles)" : "Minimum contrast 4.5:1 (text) / 3:1 (large text)"}</li>
          <li>{isFr ? "Navigation entièrement au clavier (Tab, Entrée, Échap)" : "Full keyboard navigation (Tab, Enter, Esc)"}</li>
          <li>{isFr ? "Étiquettes ARIA sur tous les éléments interactifs" : "ARIA labels on all interactive elements"}</li>
          <li>{isFr ? "Attributs data-testid pour tous les composants critiques" : "data-testid attributes on all critical components"}</li>
          <li>{isFr ? "Thème sombre / clair au choix, préférence système respectée" : "Dark / light theme, respects system preference"}</li>
          <li>{isFr ? "Textes redimensionnables jusqu'à 200 % sans perte de contenu" : "Text resizable up to 200% without loss of content"}</li>
          <li>{isFr ? "Bilingue français / anglais (Loi 96)" : "Bilingual French / English (Law 96)"}</li>
        </ul>
      </Section>
      <Section title={isFr ? "Limitations connues" : "Known limitations"}>
        <p>{isFr
          ? "Certains graphiques d'analyse restent en cours d'amélioration pour la compatibilité lecteur d'écran. Une alternative en tableau textuel est disponible."
          : "Some analytics charts are still being improved for screen reader compatibility. A textual table alternative is available."}
        </p>
      </Section>
      <Section title={isFr ? "Nous signaler un obstacle" : "Report an issue"}>
        <p>
          {isFr ? "Écrivez-nous à " : "Contact us at "}
          <a href={`mailto:${PROGERE_LEGAL.entity.supportEmail}?subject=Accessibilit%C3%A9`} style={{ color: "var(--pg-gold)" }} data-testid="a11y-report-link">
            {PROGERE_LEGAL.entity.supportEmail}
          </a>
          {isFr ? ". Nous vous répondrons dans un délai de 10 jours ouvrables." : ". We will reply within 10 business days."}
        </p>
      </Section>
    </LegalShell>
  );
}

// ============================================================================
// MENTIONS LÉGALES
// ============================================================================
export function LegalNoticePage() {
  const { lang } = useApp();
  const isFr = lang === "fr";
  const cfg = PROGERE_LEGAL;
  return (
    <LegalShell icon={Building2} title={isFr ? "Mentions légales" : "Legal notice"} testId="page-legal-notice">
      <Section title={isFr ? "Éditeur" : "Publisher"}>
        <p>
          <strong>{cfg.entity.legalName}</strong><br />
          {isFr ? "Nom commercial : " : "Trade name: "} {cfg.entity.tradeName}<br />
          {cfg.address.line1}<br />
          {isFr ? "Courriel : " : "Email: "}
          <a href={`mailto:${cfg.entity.supportEmail}`} style={{ color: "var(--pg-gold)" }}>{cfg.entity.supportEmail}</a>
        </p>
      </Section>
      <Section title={isFr ? "Écosystème" : "Ecosystem"}>
        <p>{isFr
          ? `PROGÈRE Devis AI fait partie de l'écosystème PROGÈRE, propulsé par ${cfg.entity.poweredBy}. Les autres modules incluent notamment PROGÈRE CORE, PUNCH, CRM, ERP, COMPTA, PAIE et TAKEOFF.`
          : `PROGÈRE Devis AI is part of the PROGÈRE ecosystem, powered by ${cfg.entity.poweredBy}. Other modules include PROGÈRE CORE, PUNCH, CRM, ERP, COMPTA, PAIE and TAKEOFF.`}
        </p>
      </Section>
      <Section title={isFr ? "Hébergement" : "Hosting"}>
        <p>{isFr
          ? "Infrastructure cloud sur MongoDB Atlas (région Montréal, Canada) et services applicatifs répartis en Amérique du Nord."
          : "Cloud infrastructure on MongoDB Atlas (Montreal, Canada region) and application services distributed in North America."}
        </p>
      </Section>
      <Section title={isFr ? "Propriété intellectuelle" : "Intellectual property"}>
        <p>{isFr
          ? `Toutes les marques, logos, textes, images, code source et bases de données présents sur ${cfg.entity.website} sont la propriété exclusive de ${cfg.entity.legalName}. Toute reproduction, représentation, modification ou adaptation, totale ou partielle, sans autorisation écrite est interdite.`
          : `All trademarks, logos, texts, images, source code and databases on ${cfg.entity.website} are the exclusive property of ${cfg.entity.legalName}. Any reproduction, representation, modification or adaptation, in whole or in part, without written authorization is prohibited.`}
        </p>
      </Section>
      <Section title={isFr ? "Loi applicable" : "Applicable law"}>
        <p>{isFr
          ? `Les présentes mentions et l'utilisation du service sont régies par les lois de la province de Québec et du Canada. Tout litige relève de la compétence exclusive des tribunaux du ${cfg.address.jurisdiction}.`
          : `These notices and the use of the service are governed by the laws of the province of Quebec and Canada. Any dispute falls under the exclusive jurisdiction of the courts of ${cfg.address.jurisdiction}.`}
        </p>
      </Section>
    </LegalShell>
  );
}

// ============================================================================
// SÉCURITÉ
// ============================================================================
export function SecurityPage() {
  const { lang } = useApp();
  const isFr = lang === "fr";
  return (
    <LegalShell icon={Lock} title={isFr ? "Sécurité & confiance" : "Security & trust"} testId="page-security">
      <Section title={isFr ? "Mesures techniques" : "Technical measures"}>
        <ul className="list-disc pl-6 space-y-1">
          <li>{isFr ? "Chiffrement TLS 1.2+ sur toutes les communications" : "TLS 1.2+ encryption on all communications"}</li>
          <li>{isFr ? "Mots de passe hachés avec bcrypt (cost ≥ 12)" : "Passwords hashed with bcrypt (cost ≥ 12)"}</li>
          <li>{isFr ? "Authentification à deux facteurs (2FA) par courriel" : "Two-factor authentication (2FA) by email"}</li>
          <li>{isFr ? "Jetons JWT signés HS256 avec expiration courte" : "HS256-signed JWT tokens with short expiration"}</li>
          <li>{isFr ? "Isolation stricte multi-tenant (filtre user_id sur toute requête)" : "Strict multi-tenant isolation (user_id filter on every query)"}</li>
          <li>{isFr ? "Journalisation des actions sensibles (audit trail)" : "Audit logging of sensitive actions"}</li>
          <li>{isFr ? "Signature HMAC des webhooks Stripe" : "HMAC signing of Stripe webhooks"}</li>
        </ul>
      </Section>
      <Section title={isFr ? "Sous-traitants (Loi 25 art. 17)" : "Subprocessors (Law 25 art. 17)"}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead style={{ background: "var(--pg-bg-tertiary)" }}>
              <tr>
                <th className="text-left px-3 py-2" style={{ color: "var(--pg-text-secondary)" }}>{isFr ? "Fournisseur" : "Provider"}</th>
                <th className="text-left px-3 py-2" style={{ color: "var(--pg-text-secondary)" }}>{isFr ? "Rôle" : "Purpose"}</th>
                <th className="text-left px-3 py-2" style={{ color: "var(--pg-text-secondary)" }}>{isFr ? "Localisation" : "Location"}</th>
                <th className="text-left px-3 py-2" style={{ color: "var(--pg-text-secondary)" }}>{isFr ? "Certifications" : "Compliance"}</th>
              </tr>
            </thead>
            <tbody>
              {PROGERE_LEGAL.subprocessors.map((sp) => (
                <tr key={sp.name} style={{ borderTop: "1px solid var(--pg-border)" }}>
                  <td className="px-3 py-2 font-semibold" style={{ color: "var(--pg-text-primary)" }}>{sp.name}</td>
                  <td className="px-3 py-2" style={{ color: "var(--pg-text-secondary)" }}>{isFr ? sp.purpose.fr : sp.purpose.en}</td>
                  <td className="px-3 py-2" style={{ color: "var(--pg-text-secondary)" }}>{sp.location}</td>
                  <td className="px-3 py-2" style={{ color: "var(--pg-text-muted)" }}>{sp.compliance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      <Section title={isFr ? "Signaler une faille (Divulgation responsable)" : "Report a vulnerability (Responsible disclosure)"}>
        <p>{isFr
          ? "Nous encourageons la divulgation responsable. Écrivez à "
          : "We encourage responsible disclosure. Please write to "}
          <a href={`mailto:${PROGERE_LEGAL.dpo.email}?subject=Vuln%C3%A9rabilit%C3%A9`} style={{ color: "var(--pg-gold)" }} data-testid="security-report-link">
            {PROGERE_LEGAL.dpo.email}
          </a>
          {isFr ? ". Nous accusons réception sous 72 heures." : ". We acknowledge receipt within 72 hours."}
        </p>
      </Section>
      <Section title={isFr ? "En cas d'incident" : "In case of an incident"}>
        <p>{isFr
          ? "En cas d'incident de confidentialité présentant un risque sérieux de préjudice, nous avisons la Commission d'accès à l'information (CAI) et les personnes concernées, conformément à l'article 3.5 de la Loi 25. Un registre des incidents est conservé pendant 5 ans."
          : "In case of a privacy incident presenting a serious risk of harm, we notify the Access to Information Commission (CAI) and affected persons, in accordance with Article 3.5 of Law 25. An incident registry is kept for 5 years."}
        </p>
      </Section>
    </LegalShell>
  );
}

// ============================================================================
// DEMANDE DE CONFIDENTIALITÉ (formulaire — Loi 25 art. 27-33)
// ============================================================================
export function PrivacyRequestPage() {
  const { lang } = useApp();
  const isFr = lang === "fr";
  const [form, setForm] = useState({
    request_type: "access",
    full_name: "",
    email: "",
    details: "",
    identity_confirmation: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.identity_confirmation) {
      setResult({ ok: false, msg: isFr ? "Vous devez confirmer votre identité." : "You must confirm your identity." });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/legal/privacy-requests`, form);
      setResult({ ok: true, ref: data.reference, msg: isFr
        ? `Demande enregistrée. Référence : ${data.reference}. Nous répondrons dans un délai maximal de 30 jours.`
        : `Request registered. Reference: ${data.reference}. We will reply within 30 days.` });
      setForm({ request_type: "access", full_name: "", email: "", details: "", identity_confirmation: false });
    } catch (err) {
      setResult({ ok: false, msg: err?.response?.data?.detail || (isFr ? "Erreur lors de l'envoi." : "Submission error.") });
    } finally { setSubmitting(false); }
  };

  return (
    <LegalShell icon={ScrollText} title={isFr ? "Exercer mes droits (Loi 25)" : "Exercise my rights (Law 25)"} testId="page-privacy-request">
      <Section title={isFr ? "Vos droits" : "Your rights"}>
        <p>{isFr
          ? "Conformément à la Loi 25 (RLRQ c. P-39.1), vous pouvez à tout moment demander l'accès, la rectification, la suppression, la portabilité de vos renseignements personnels, ou retirer votre consentement. Nous répondrons dans un délai maximal de 30 jours."
          : "Under Law 25 (RLRQ c. P-39.1), you may at any time request access, rectification, deletion, or portability of your personal information, or withdraw your consent. We will reply within 30 days."}
        </p>
      </Section>

      <form onSubmit={submit} className="space-y-4" data-testid="privacy-request-form">
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest mb-1.5 block" style={{ color: "var(--pg-text-muted)" }}>
            {isFr ? "Type de demande" : "Request type"}
          </label>
          <select
            data-testid="privacy-request-type"
            value={form.request_type}
            onChange={(e) => setForm((f) => ({ ...f, request_type: e.target.value }))}
            className="w-full progere-select"
          >
            {PROGERE_LEGAL.privacyRequestTypes.map((t) => (
              <option key={t.id} value={t.id}>{isFr ? t.labelFr : t.labelEn}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest mb-1.5 block" style={{ color: "var(--pg-text-muted)" }}>
              {isFr ? "Nom complet" : "Full name"}
            </label>
            <input
              data-testid="privacy-request-name"
              required
              value={form.full_name}
              onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
              className="w-full progere-input"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest mb-1.5 block" style={{ color: "var(--pg-text-muted)" }}>
              {isFr ? "Courriel de contact" : "Contact email"}
            </label>
            <input
              data-testid="privacy-request-email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="w-full progere-input"
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest mb-1.5 block" style={{ color: "var(--pg-text-muted)" }}>
            {isFr ? "Détails / précisions" : "Details"}
          </label>
          <textarea
            data-testid="privacy-request-details"
            rows={5}
            value={form.details}
            onChange={(e) => setForm((f) => ({ ...f, details: e.target.value }))}
            className="w-full progere-textarea"
            placeholder={isFr ? "Décrivez brièvement votre demande…" : "Briefly describe your request…"}
          />
        </div>
        <label className="flex items-start gap-2 cursor-pointer" data-testid="privacy-request-identity">
          <input
            type="checkbox"
            checked={form.identity_confirmation}
            onChange={(e) => setForm((f) => ({ ...f, identity_confirmation: e.target.checked }))}
            className="mt-1"
            style={{ accentColor: "var(--pg-gold)" }}
          />
          <span className="text-xs" style={{ color: "var(--pg-text-secondary)" }}>
            {isFr
              ? "Je confirme que les informations ci-dessus sont exactes et que je suis la personne concernée (ou son représentant autorisé). Une preuve d'identité pourra être demandée conformément à la Loi 25."
              : "I confirm the above information is accurate and that I am the person concerned (or their authorized representative). Proof of identity may be requested under Law 25."}
          </span>
        </label>
        <button
          type="submit"
          disabled={submitting}
          data-testid="privacy-request-submit"
          className="text-sm font-semibold px-5 py-2.5 rounded-full inline-flex items-center gap-2 disabled:opacity-60"
          style={{ background: "var(--pg-gold)", color: "var(--pg-text-inverse)" }}
        >
          <Send size={14} /> {submitting ? (isFr ? "Envoi…" : "Sending…") : (isFr ? "Envoyer ma demande" : "Send request")}
        </button>

        {result && (
          <div
            data-testid="privacy-request-result"
            className="rounded-xl p-4 flex items-start gap-2"
            style={{
              background: result.ok ? "color-mix(in srgb, var(--pg-success) 10%, transparent)" : "color-mix(in srgb, var(--pg-danger) 10%, transparent)",
              border: `1px solid ${result.ok ? "var(--pg-success)" : "var(--pg-danger)"}`,
              color: "var(--pg-text-primary)",
            }}
          >
            {result.ok ? <Check size={16} style={{ color: "var(--pg-success)" }} /> : <AlertCircle size={16} style={{ color: "var(--pg-danger)" }} />}
            <span className="text-sm">{result.msg}</span>
          </div>
        )}
      </form>

      <div className="mt-8 text-xs" style={{ color: "var(--pg-text-muted)" }}>
        {isFr ? "Ou par courriel direct : " : "Or by direct email: "}
        <a href={`mailto:${PROGERE_LEGAL.dpo.email}`} style={{ color: "var(--pg-gold)" }}>{PROGERE_LEGAL.dpo.email}</a>
      </div>
    </LegalShell>
  );
}
